/**
 * Every CSS class name used by components lives here, grouped by component.
 * Components import from this file instead of typing class strings inline,
 * so renaming a class is a one-line change (plus the matching CSS file).
 */

export const APP_CLASSES = Object.freeze({
  root: 'app',
  main: 'app__main',
});

export const HEADER_CLASSES = Object.freeze({
  root: 'site-header',
  brand: 'site-header__brand',
  logo: 'site-header__logo',
  title: 'site-header__title',
  nav: 'site-header__nav',
  navLink: 'site-header__nav-link',
  navLinkActive: 'site-header__nav-link--active',
  menuToggle: 'site-header__menu-toggle',
  navOpen: 'site-header__nav--open',
});

export const DONATE_CLASSES = Object.freeze({
  button: 'donate-button',
  icon: 'donate-button__icon',
});

export const LAB_CLASSES = Object.freeze({
  root: 'lab',
  intro: 'lab__intro',
  heading: 'lab__heading',
  subheading: 'lab__subheading',
  bench: 'lab__bench',
});

export const MIXING_CARD_CLASSES = Object.freeze({
  root: 'mixing-card',
  stageRow: 'mixing-card__stage-row',
  sceneWrapper: 'mixing-card__scene',
  gif: 'mixing-card__gif',
  speechWrapper: 'mixing-card__speech',
});

export const SCENE_CLASSES = Object.freeze({
  root: 'lab-scene',
  mixing: 'lab-scene--mixing',
  talking: 'lab-scene--talking',
  wall: 'lab-scene__wall',
  floor: 'lab-scene__floor',
  shelf: 'lab-scene__shelf',
  table: 'lab-scene__table',
  kid: 'lab-scene__kid',
  kidFrightened: 'lab-scene__kid--frightened',
  goggles: 'lab-scene__goggles',
  mouth: 'lab-scene__mouth',
  eyesOpen: 'lab-scene__eyes-open',
  eyesClosed: 'lab-scene__eyes-closed',
  hand: 'lab-scene__hand',
  handLeft: 'lab-scene__hand--left',
  handRight: 'lab-scene__hand--right',
  flask: 'lab-scene__flask',
  flaskLabel: 'lab-scene__flask-label',
  drip: 'lab-scene__drip',
  dripLeft: 'lab-scene__drip--left',
  dripRight: 'lab-scene__drip--right',
  beaker: 'lab-scene__beaker',
  beakerLiquid: 'lab-scene__beaker-liquid',
  effect: 'lab-scene__effect',
  effectPrefix: 'lab-scene__effect--',
});

export const FIZZY_CLASSES = Object.freeze({
  root: 'fizzy-speech',
  name: 'fizzy-speech__name',
  bubble: 'fizzy-speech__bubble',
  title: 'fizzy-speech__title',
  text: 'fizzy-speech__text',
  funFact: 'fizzy-speech__fun-fact',
  safety: 'fizzy-speech__safety',
  actions: 'fizzy-speech__actions',
  actionButton: 'fizzy-speech__action',
});

export const READ_ALOUD_SWITCH_CLASSES = Object.freeze({
  root: 'read-aloud-switch',
  on: 'read-aloud-switch--on',
  icon: 'read-aloud-switch__icon',
  label: 'read-aloud-switch__label',
  track: 'read-aloud-switch__track',
  thumb: 'read-aloud-switch__thumb',
  state: 'read-aloud-switch__state',
});

export const ELEMENT_SEARCH_CLASSES = Object.freeze({
  root: 'element-search',
  field: 'element-search__field',
  icon: 'element-search__icon',
  input: 'element-search__input',
  clearButton: 'element-search__clear',
  status: 'element-search__status',
});

export const TILE_CLASSES = Object.freeze({
  root: 'substance-tile',
  selected: 'substance-tile--selected',
  dimmed: 'substance-tile--dimmed',
  searchMatch: 'substance-tile--search-match',
  number: 'substance-tile__number',
  symbol: 'substance-tile__symbol',
  name: 'substance-tile__name',
  categoryPrefix: 'substance-tile--',
});

export const BEAKER_CLASSES = Object.freeze({
  root: 'mixing-bench',
  slots: 'mixing-bench__slots',
  slot: 'mixing-bench__slot',
  slotEmpty: 'mixing-bench__slot--empty',
  slotRemove: 'mixing-bench__slot-remove',
  plus: 'mixing-bench__plus',
  actions: 'mixing-bench__actions',
  mixButton: 'mixing-bench__mix-button',
  resetButton: 'mixing-bench__reset-button',
  hint: 'mixing-bench__hint',
});

export const SUMMARY_CLASSES = Object.freeze({
  root: 'reaction-summary',
  equation: 'reaction-summary__equation',
  product: 'reaction-summary__product',
  conditions: 'reaction-summary__conditions',
});

export const PERIODIC_TABLE_CLASSES = Object.freeze({
  root: 'periodic-table',
  header: 'periodic-table__header',
  title: 'periodic-table__title',
  legend: 'periodic-table__legend',
  legendButton: 'periodic-table__legend-button',
  legendButtonActive: 'periodic-table__legend-button--active',
  legendSwatch: 'periodic-table__legend-swatch',
  bottles: 'periodic-table__bottles',
  bottlesLabel: 'periodic-table__bottles-label',
  scroll: 'periodic-table__scroll',
  grid: 'periodic-table__grid',
  familyMarker: 'periodic-table__family-marker',
  familyRowLabel: 'periodic-table__family-row-label',
  familyMarkerPrefix: 'periodic-table__family-marker--',
  swipeHint: 'periodic-table__swipe-hint',
});

export const ABOUT_CLASSES = Object.freeze({
  root: 'about',
  hero: 'about__hero',
  heading: 'about__heading',
  lead: 'about__lead',
  cards: 'about__cards',
  card: 'about__card',
  cardIcon: 'about__card-icon',
  cardArt: 'about__card-art',
  cardArtWide: 'about__card-art-wide',
  cardTitle: 'about__card-title',
  story: 'about__story',
  storySection: 'about__story-section',
  storyTitle: 'about__story-title',
  sourceList: 'about__sources',
  donateBox: 'about__donate',
});

export const FOOTER_CLASSES = Object.freeze({
  root: 'site-footer',
  links: 'site-footer__links',
  link: 'site-footer__link',
  text: 'site-footer__text',
});

// Shared by the text pages: elements, reactions, privacy, terms, contact, 404.
export const CONTENT_CLASSES = Object.freeze({
  root: 'content-page',
  breadcrumbs: 'content-page__breadcrumbs',
  breadcrumbLink: 'content-page__breadcrumb-link',
  hero: 'content-page__hero',
  heading: 'content-page__heading',
  lead: 'content-page__lead',
  meta: 'content-page__meta',
  section: 'content-page__section',
  sectionTitle: 'content-page__section-title',
  text: 'content-page__text',
  factGrid: 'content-page__facts',
  fact: 'content-page__fact',
  factLabel: 'content-page__fact-label',
  factValue: 'content-page__fact-value',
  list: 'content-page__list',
  listItem: 'content-page__list-item',
  listTitle: 'content-page__list-title',
  listDetail: 'content-page__list-detail',
  equation: 'content-page__equation',
  cardGrid: 'content-page__cards',
  card: 'content-page__card',
  cardSymbol: 'content-page__card-symbol',
  cardName: 'content-page__card-name',
  button: 'content-page__button',
  buttonRow: 'content-page__buttons',
  safety: 'content-page__safety',
  funFact: 'content-page__fun-fact',
  pager: 'content-page__pager',
  quiz: 'content-page__quiz',
  quizItem: 'content-page__quiz-item',
  quizQuestion: 'content-page__quiz-question',
  quizAnswer: 'content-page__quiz-answer',
  showAll: 'content-page__show-all',
  showAllToggle: 'content-page__show-all-toggle',
  categoryPrefix: 'content-page__card--',
});

export const LAB_GUIDE_CLASSES = Object.freeze({
  root: 'lab-guide',
  heading: 'lab-guide__heading',
  steps: 'lab-guide__steps',
  step: 'lab-guide__step',
  faq: 'lab-guide__faq',
  question: 'lab-guide__question',
  answer: 'lab-guide__answer',
  links: 'lab-guide__links',
});

// Printable worksheets (styles/Worksheet.css, which also holds the print rules).
export const WORKSHEET_CLASSES = Object.freeze({
  page: 'worksheet-page',
  noPrint: 'worksheet-page__no-print',
  actions: 'worksheet-page__actions',
  printButton: 'worksheet-page__print-button',
  sheet: 'worksheet',
  nameLine: 'worksheet__name-line',
  part: 'worksheet__part',
  partTitle: 'worksheet__part-title',
  table: 'worksheet__table',
  checkbox: 'worksheet__checkbox',
  blank: 'worksheet__blank',
  blankWide: 'worksheet__blank--wide',
  answer: 'worksheet__answer',
  answerList: 'worksheet__answer-list',
  wordBank: 'worksheet__word-bank',
  columns: 'worksheet__columns',
  column: 'worksheet__column',
  columnTitle: 'worksheet__column-title',
  writeArea: 'worksheet__write-area',
  questions: 'worksheet__questions',
  question: 'worksheet__question',
  entry: 'worksheet__entry',
  field: 'worksheet__field',
  fieldLabel: 'worksheet__field-label',
  footnote: 'worksheet__footnote',
});

export const AD_CLASSES = Object.freeze({
  root: 'ad-slot',
  label: 'ad-slot__label',
  unit: 'adsbygoogle',
});
